import React, { useState } from 'react'
import { Checkbox, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { moment } from '@BUP/configs/i18n'
import styles from './period-dashboard.module.scss'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import AlertComponent from '@BUP/components/Generics/Alert'
import ModalComponent from '@BUP/components/Generics/Modal'
import BupSpinner from '@BUP/components/Loading/Spinner'

const { Option } = Select

/**
 * Minimal counselor-facing control for the "Asignación de Cursos" preferences period: pick a
 * grade, open/reopen/close its period, see how many students have completed it.
 *
 * A period scopes exactly one grade, and different grades can have their own period open at the
 * same time (see the backend's openPeriod doc comment) - so the grade selector isn't just for
 * opening a new period, it's how the counselor picks WHICH grade's period this card shows.
 * Switching it re-queries `period` for that grade (see the parent page).
 */
const PeriodDashboard = ({
  isLoading,
  error,
  period,
  grades,
  isLoadingGrades,
  selectedGrade,
  onSelectGrade,
  onOpen,
  onClose,
  isOpening,
  isClosing
}) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  // Set once per period, at the moment it's opened (see openPeriod on the backend) - not editable
  // afterward, so this only needs to live here, not lifted to the parent page.
  const [allowOtherCourses, setAllowOtherCourses] = useState(true)
  // Closing a period, and opening a new one on top of a closed one, both need an explicit
  // confirmation: the new period starts empty, so every student who had already answered sees a
  // blank form again (in production a period with 78 answers was closed and replaced this way by
  // mistake). 'close' | 'openNew' | null.
  const [pendingConfirmation, setPendingConfirmation] = useState(null)

  const handleConfirm = async () => {
    try {
      if (pendingConfirmation === 'close') await onClose(period.id)
      if (pendingConfirmation === 'openNew') await onOpen({ grade: selectedGrade, allowOtherCourses })
    } finally {
      setPendingConfirmation(null)
    }
  }

  if (error) {
    return (
      <AlertComponent
        type='error'
        messageKey='common.resources.course-assignment.actions.read.failure'
        error={error}
      />
    )
  }

  const isOpen = period?.status === 'open'

  return (
    <div className={styles.container}>
      <div className={styles.buttons_wrapper}>
        <label className={styles.grade_select_label} htmlFor='course-assignment-grade-select'>
          {t('gradeSelectLabel')}
        </label>
        <Select
          id='course-assignment-grade-select'
          placeholder={t('gradeSelectPlaceholder')}
          value={selectedGrade}
          onChange={onSelectGrade}
          loading={isLoadingGrades}
          style={{ minWidth: 200 }}
        >
          {(grades || []).map(grade => <Option key={grade} value={grade}>{grade}</Option>)}
        </Select>
      </div>

      {isLoading
        ? <BupSpinner size='small' />
        : (
          <div className={styles.status_wrapper}>
            {!period && <p className={styles.status_text}>{t('noPeriod')}</p>}
            {period && (
              <>
                <p className={styles.status_text}>
                  {isOpen ? t('statusOpen') : t('statusClosed')}
                </p>
                {period.grade && (
                  <p className={styles.completion_text}>{t('periodGrade', { grade: period.grade })}</p>
                )}
                <p className={styles.completion_text}>
                  {period.allowOtherCourses ? t('allowOtherCoursesStatusYes') : t('allowOtherCoursesStatusNo')}
                </p>
                {isOpen && period.opensAt && (
                  <p className={styles.completion_text}>
                    {t('openedAt', { date: moment(period.opensAt).format('lll') })}
                  </p>
                )}
                {!isOpen && period.closedAt && (
                  <p className={styles.completion_text}>
                    {t('closedAt', { date: moment(period.closedAt).format('lll') })}
                  </p>
                )}
                <p className={styles.completion_text}>
                  {t('completionCount', {
                    completed: period.completedCount ?? 0,
                    eligible: period.eligibleCount ?? 0
                  })}
                </p>
              </>
            )}
          </div>
          )}

      {!isOpen && (
        <div className={styles.buttons_wrapper}>
          <Checkbox
            checked={allowOtherCourses}
            onChange={event => setAllowOtherCourses(event.target.checked)}
          >
            {t('allowOtherCoursesCheckboxLabel')}
          </Checkbox>
        </div>
      )}

      <div className={styles.buttons_wrapper}>
        {isOpen
          ? (
            <ButtonComponent
              buttonText={t('closeButton')}
              colorType='primary'
              onClick={() => setPendingConfirmation('close')}
              disabled={isClosing}
            />
            )
          : (
            <ButtonComponent
              buttonText={t(period ? 'reopenButton' : 'openButton')}
              colorType='primary'
              onClick={() => period
                ? setPendingConfirmation('openNew')
                : onOpen({ grade: selectedGrade, allowOtherCourses })}
              disabled={isOpening || !selectedGrade}
            />
            )}
      </div>

      <ModalComponent
        stateModal={!!pendingConfirmation}
        onClickCloseModal={() => setPendingConfirmation(null)}
        modalTitle={pendingConfirmation === 'close' ? t('closeConfirmTitle') : t('openNewConfirmTitle')}
        modalDescription={
          <p>
            {pendingConfirmation === 'close'
              ? t('closeConfirmDescription', { count: period?.completedCount ?? 0 })
              : t('openNewConfirmDescription', { count: period?.completedCount ?? 0 })}
          </p>
        }
        confirmButtonText={pendingConfirmation === 'close' ? t('closeConfirmButton') : t('openNewConfirmButton')}
        loading={isClosing || isOpening}
        onClickConfirm={handleConfirm}
        dataTest='course-assignment-period-confirm'
      />
    </div>
  )
}

export default PeriodDashboard
